<template>
  <Transition :css="false" @enter="onEnter" @leave="onLeave">
    <slot />
  </Transition>
</template>

<script setup>
// Drop-in replacement for a plain fade around a block that mounts/unmounts
// inside a column. A fade alone still yanks the node out of the DOM at full
// height, so everything below snaps upward; this collapses the box (height,
// vertical margin and padding) over the same beat, letting the siblings slide
// into place.
import { MOTION_EASING, motionDuration } from '~/utils/motion'

// The element's resting box, as explicit pixel values to animate from/to.
const expandedBox = (el) => {
  const style = getComputedStyle(el)
  return {
    // Tailwind's preflight sets box-sizing: border-box, so offsetHeight is the
    // same box the `height` property addresses.
    height: `${el.offsetHeight}px`,
    marginTop: style.marginTop,
    marginBottom: style.marginBottom,
    paddingTop: style.paddingTop,
    paddingBottom: style.paddingBottom,
    opacity: 1,
  }
}

const COLLAPSED_BOX = {
  height: '0px',
  marginTop: '0px',
  marginBottom: '0px',
  paddingTop: '0px',
  paddingBottom: '0px',
  opacity: 0,
}

const run = (el, keyframes, fill, done) => {
  // An interrupted transition (re-entering mid-leave) leaves a stale animation
  // behind; drop it so only the current direction is composited.
  el._collapse?.cancel()

  const previousOverflow = el.style.overflow
  el.style.overflow = 'hidden'

  const animation = el.animate(keyframes, { duration: motionDuration(), easing: MOTION_EASING, fill })
  el._collapse = animation

  animation.onfinish = () => {
    el._collapse = undefined
    el.style.overflow = previousOverflow
    done()
  }
  // Vue reports cancellation to its own hooks; just undo what we changed.
  animation.oncancel = () => {
    el.style.overflow = previousOverflow
  }
}

const onEnter = (el, done) => run(el, [COLLAPSED_BOX, expandedBox(el)], 'none', done)
// `forwards` holds the collapsed box until Vue removes the node, so there's no
// frame where it flashes back to full height.
const onLeave = (el, done) => run(el, [expandedBox(el), COLLAPSED_BOX], 'forwards', done)
</script>
